import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';

import { ImageService, ImageNodeAttachmentMetadata } from '../../_service/image.service';
import { AttachmentImageInsertPayload, LinkedImageInsertPayload } from './extensions/phase1-image';

export type ImageInsertMode = 'url' | 'upload';

@Component({
  selector: 'app-image-insert-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, InputTextModule, ButtonModule],
  template: `
    <p-dialog
      header="Insert Image"
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      (onHide)="onDialogHide()"
      [style]="{ width: '480px', maxWidth: '92vw' }"
      appendTo="body">

      <!-- Mode selector -->
      <div class="flex gap-2 mb-4 border-b border-surface-200 pb-3">
        <button
          type="button"
          class="px-3 py-1.5 rounded text-sm font-medium transition-colors"
          [class.bg-primary-600]="mode === 'url'"
          [class.text-white]="mode === 'url'"
          [class.bg-surface-100]="mode !== 'url'"
          [class.text-surface-700]="mode !== 'url'"
          (click)="setMode('url')">
          From URL
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded text-sm font-medium transition-colors"
          [class.bg-primary-600]="mode === 'upload'"
          [class.text-white]="mode === 'upload'"
          [class.bg-surface-100]="mode !== 'upload'"
          [class.text-surface-700]="mode !== 'upload'"
          (click)="setMode('upload')">
          Upload
        </button>
      </div>

      <!-- URL mode -->
      <div *ngIf="mode === 'url'" class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label for="img-url" class="text-sm font-semibold">Image URL</label>
          <input
            id="img-url"
            pInputText
            type="url"
            [(ngModel)]="imageUrl"
            placeholder="https://example.com/image.png"
            class="w-full"
            autofocus />
        </div>
        <div *ngIf="urlError" class="text-sm text-red-600">{{ urlError }}</div>
        <div *ngIf="urlProbing" class="text-sm text-surface-600 flex items-center gap-2">
          <span class="material-icons text-base animate-spin">sync</span>
          Loading image…
        </div>
      </div>

      <!-- Upload mode -->
      <div *ngIf="mode === 'upload'" class="flex flex-col gap-3">
        <div *ngIf="!noteId" class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Upload is only available when editing a saved note.
        </div>
        <div *ngIf="noteId" class="flex flex-col gap-2">
          <label class="text-sm font-semibold">Select image file</label>
          <input
            #fileInput
            type="file"
            accept="image/*"
            class="block w-full text-sm text-surface-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            (change)="onFileSelected($event)" />
          <div *ngIf="selectedFile" class="text-xs text-surface-500">
            {{ selectedFile.name }} ({{ formatBytes(selectedFile.size) }})
          </div>
        </div>
        <div *ngIf="uploadError" class="text-sm text-red-600">{{ uploadError }}</div>
        <div *ngIf="uploading" class="text-sm text-surface-600 flex items-center gap-2">
          <span class="material-icons text-base animate-spin">sync</span>
          Uploading...
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex gap-2 justify-end">
          <p-button
            label="Cancel"
            severity="secondary"
            (onClick)="onCancel()">
          </p-button>
          <p-button
            *ngIf="mode === 'url'"
            label="Insert"
            [disabled]="!imageUrl.trim() || urlProbing"
            [loading]="urlProbing"
            (onClick)="onInsertUrl()">
          </p-button>
          <p-button
            *ngIf="mode === 'upload'"
            label="Upload & Insert"
            [disabled]="!selectedFile || !noteId || uploading"
            [loading]="uploading"
            (onClick)="onUpload()">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
})
export class ImageInsertDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() noteId: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() insertLinked = new EventEmitter<LinkedImageInsertPayload>();
  @Output() insertAttachment = new EventEmitter<AttachmentImageInsertPayload>();
  @Output() cancel = new EventEmitter<void>();

  mode: ImageInsertMode = 'url';
  imageUrl = '';
  urlError = '';
  urlProbing = false;

  selectedFile: File | null = null;
  uploading = false;
  uploadError = '';

  constructor(private imageService: ImageService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.reset();
    }
  }

  setMode(m: ImageInsertMode): void {
    this.mode = m;
    this.urlError = '';
    this.uploadError = '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
    this.uploadError = '';
  }

  onInsertUrl(): void {
    const url = this.imageUrl.trim();
    if (!url) {
      this.urlError = 'Please enter a URL.';
      return;
    }
    try {
      new URL(url);
    } catch {
      this.urlError = 'Please enter a valid URL.';
      return;
    }

    this.urlError = '';
    this.urlProbing = true;

    // First try CORS-enabled probing; if that fails, retry a plain load so users
    // can still insert displayable cross-origin images.
    this.probeLinkedImage(url, true)
      .catch(() => this.probeLinkedImage(url, false))
      .then(({ naturalWidth, naturalHeight }) => {
        this.urlProbing = false;
        const { width, height } = this.computeInitialDisplaySize(naturalWidth, naturalHeight);
        this.insertLinked.emit({
          url,
          naturalWidth,
          naturalHeight,
          width,
          height,
        });
        this.closeDialog();
      })
      .catch(() => {
        this.urlProbing = false;
        this.urlError = 'Could not load image. The host may block access or be temporarily unavailable.';
      });
  }

  private probeLinkedImage(url: string, useCorsAnonymous: boolean): Promise<{ naturalWidth: number | null; naturalHeight: number | null }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (useCorsAnonymous) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        const nw = img.naturalWidth > 0 ? img.naturalWidth : null;
        const nh = img.naturalHeight > 0 ? img.naturalHeight : null;
        resolve({ naturalWidth: nw, naturalHeight: nh });
      };

      img.onerror = () => {
        reject(new Error('Image load failed'));
      };

      img.src = url;
    });
  }

  private computeInitialDisplaySize(
    naturalWidth: number | null,
    naturalHeight: number | null,
  ): { width: number | null; height: number | null } {
    const MAX_DISPLAY_WIDTH = 800;
    const nw = naturalWidth ?? 0;
    const nh = naturalHeight ?? 0;

    if (nw <= 0 || nh <= 0) {
      return { width: null, height: null };
    }

    if (nw > MAX_DISPLAY_WIDTH) {
      return {
        width: MAX_DISPLAY_WIDTH,
        height: Math.round((nh / nw) * MAX_DISPLAY_WIDTH),
      };
    }

    return { width: nw, height: nh };
  }

  onUpload(): void {
    if (!this.selectedFile || !this.noteId) {
      return;
    }
    this.uploading = true;
    this.uploadError = '';
    this.imageService.uploadImage(this.selectedFile, this.noteId).subscribe({
      next: (meta: ImageNodeAttachmentMetadata) => {
        this.uploading = false;
        this.insertAttachment.emit({
          attachmentId: meta.attachmentId,
          src: meta.src,
        });
        this.closeDialog();
      },
      error: (err: unknown) => {
        this.uploading = false;
        this.uploadError = err instanceof Error ? err.message : 'Upload failed.';
      },
    });
  }

  onCancel(): void {
    this.cancel.emit();
    this.closeDialog();
  }

  onDialogHide(): void {
    if (this.visible) {
      this.visibleChange.emit(false);
    }
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private reset(): void {
    this.mode = 'url';
    this.imageUrl = '';
    this.urlError = '';
    this.urlProbing = false;
    this.selectedFile = null;
    this.uploading = false;
    this.uploadError = '';
  }
}
